import React from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Text,
    useDisclosure,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    Menu,
    MenuOptionGroup,
    MenuItemOption,
    Icon,
    IconButton
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import { GlobeDark, CustomClose, CheckIcon } from '../icons'


const CountrySelectorDrawer = ({
    countries,
    language,
    countryCode,
    handleLanguageChange,
    triggerRef,
    variant = 'header'
}) => {
    const {isOpen, onClose, onToggle} = useDisclosure()

    const getFooterLabel = (locale) => {
        if (!countries || !locale) {
            return
        }

        const [language, country] = locale.split('_')
        const localeTag = `${language}-${country}`

        const currencySymbol = new Intl.NumberFormat(localeTag, {
            style: 'currency',
            currency: countries[locale]?.currency || 'GBP'
        }).format(0).replace(/[\d.,]/g, '').trim()

        return `${countryCode} - ${language.toUpperCase()} (${currencySymbol})`
    }

    // Expose onToggle and onClose to parent via ref
    React.useEffect(() => {
        if (triggerRef) {
            triggerRef.current = {onToggle, onClose, isOpen}
        }
    }, [triggerRef, onToggle, onClose, isOpen])

    return (
        <>
            <Flex alignItems="center" pointerEvents="none">
                {variant === 'footer' ? (
                    <>
                        <Icon as={GlobeDark} boxSize={5} />
                        <Text fontSize="xs"
                            lineHeight={"15px"}
                            fontWeight={"medium"}
                            letterSpacing={"0%"}
                            paddingLeft={1}>
                            {getFooterLabel(language)}
                        </Text>
                        <Text fontSize="xs"
                            lineHeight={"15px"}
                            fontWeight={"medium"}
                            letterSpacing={"0%"}
                            textDecoration={"underline"}
                            paddingLeft={2}>
                            <FormattedMessage
                                defaultMessage="Change Country"
                                id="country_selector.change_country"
                            />
                        </Text>
                    </>
                ) : (
                    <>
                        <Text fontSize="sm"
                            lineHeight={{base: '0'}}
                            paddingBottom={{base: '2px'}}
                            sx={{
                                '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                                    marginTop: {base: '5px'},
                                    paddingBottom: {base: 0}
                                }
                            }}>
                            {countryCode}
                        </Text>
                    </>
                )}
            </Flex>
            <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
                <DrawerOverlay />
                <DrawerContent>
                    <IconButton
                        aria-label="Close"
                        icon={<CustomClose />}
                        position="absolute"
                        minW={'auto'}
                        w={{base: '24px'}}
                        h={{base: '24px'}}
                        top={{base: '18px'}}
                        right={{base: '17px'}}
                        zIndex={'2'}
                        backgroundColor="transparent"
                        _hover={{backgroundColor: 'transparent'}}
                        color="black"
                        onClick={onClose}
                    />
                    <DrawerHeader padding="24px 16px 31px" boxShadow="none">
                        <Text color="gray.700" fontSize="xs" fontWeight="400" lineHeight="100%">
                            <FormattedMessage
                                id="country_selector_drawer_title.select_country"
                                defaultMessage="Select Country"
                            />
                        </Text>
                    </DrawerHeader>
                    <DrawerBody padding="0 0 170px">
                        <Menu>
                            <MenuOptionGroup
                                defaultValue={language}
                                value={language}
                                onChange={(e) => handleLanguageChange(e)}
                            >
                                {countries &&
                                    Object.entries(countries).map(([lang, {label}], index) => {
                                        return (
                                            <MenuItemOption
                                                key={lang}
                                                value={lang}
                                                icon={language === lang ? <CheckIcon /> : null}
                                                color="black"
                                                fontSize="md"
                                                lineHeight="24px"
                                                padding="12px 16px 16px 16px"
                                                borderTop={
                                                    index === 0 ? 'none' : '1px solid #ECE9E9'
                                                }
                                            >
                                                {label}
                                            </MenuItemOption>
                                        )
                                    })}
                            </MenuOptionGroup>
                        </Menu>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}

CountrySelectorDrawer.propTypes = {
    countries: PropTypes.object,
    language: PropTypes.string,
    countryCode: PropTypes.string,
    handleLanguageChange: PropTypes.func,
    triggerRef: PropTypes.object
}

export default CountrySelectorDrawer
